// FooterOne.jsx
// Categories are fetched internally using CategoriesServices.getCategoryTree()
// — the same service used by HeaderOne. No prop is needed from the parent.
// Usage: <FooterOne />

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CategoriesServices } from "../services/CategoriesServices";
import ContactServices from "../services/contactServices";

const footerData = {
  brand: "IFJONA",
  licenseCompany: "Ifjona LLC",
  logo: "/assets/images/logo/logo-2.svg",
  address: "117-05 197th St, Saint Albans, NY 11412",
  phones: ["+1 347-854-7073"],
  email: "ifjonallc@gmail.com",
  tagline: "Your everyday marketplace — essentials to electronics, fashion to fresh groceries.",

  staticColumns: [
    {
      title: "Information",
      links: [
        { label: "About IFJONA", to: "/about" },
        { label: "Privacy Policy", to: "/privacy-policy" },
        { label: "Terms & Conditions", to: "/terms-conditions" },
        { label: "Refund Policy", to: "/refund-policy" },
        { label: "Developer", to: "/developer" },
      ],
    },
    {
      title: "Customer Support",
      links: [
        { label: "Help Center", to: "/help-center" },
        { label: "Contact Us", to: "/contact" },
        { label: "Shipping & Delivery", to: "/shipping" },
        { label: "Returns & Refunds", to: "/returns" },
      ],
    },
    {
      title: "My Account",
      links: [
        { label: "My Account", to: "/account" },
        { label: "Shopping Cart", to: "/cart" },
        { label: "Wishlist", to: "/wishlist" },
      ],
    },
  ],

  socials: [
    { href: "https://www.facebook.com/ifjona", icon: "ph-fill ph-facebook-logo", label: "Facebook" },
    { href: "https://www.x.com", icon: "ph-fill ph-x-logo", label: "X" },
    { href: "https://www.instagram.com/ifjonallc", icon: "ph-fill ph-instagram-logo", label: "Instagram" },
    { href: "https://www.tiktok.com/@ifjona", icon: "ph-fill ph-tiktok-logo", label: "TikTok" },
  ],

  paymentImg: "assets/images/thumbs/BottomFooter/payment-method.png",
  year: new Date().getFullYear(),
};

/* ─── Collapsible column for mobile ─── */
const FooterColumn = ({ title, links }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="ifjona-footer-col">
      <button
        className="ifjona-footer-col__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="ifjona-footer-col__title">{title}</span>
        <i className={`ph-bold ${open ? "ph-minus" : "ph-plus"} ifjona-footer-col__caret`} />
      </button>
      <ul className={`ifjona-footer-col__list ${open ? "ifjona-footer-col__list--open" : ""}`}>
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="ifjona-footer-col__link">
              <i className="ph-bold ph-caret-right ifjona-footer-col__link-caret" />
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ─── Main Footer ─── */
const FooterOne = () => {
  const {
    brand,
    licenseCompany,
    logo,
    tagline,
    address,
    phones,
    email,
    staticColumns,
    socials,
    paymentImg,
    year,
  } = footerData;

  const [dbCategories, setDbCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [contactData, setContactData] = useState(null);
  const [contactLoading, setContactLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCatLoading(true);
        const data = await CategoriesServices.getCategoryTree();
        setDbCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("FooterOne: failed to load categories", err);
        setDbCategories([]);
      } finally {
        setCatLoading(false);
      }
    };
    const fetchContacts = async () => {
      try {
        setContactLoading(true);
        const data = await ContactServices.getAllContacts();
        setContactData(data[0] || null);
      } catch (err) {
        console.error("FooterOne: failed to load contact info", err);
        setContactData(null);
      } finally {
        setContactLoading(false);
      }
    };
    fetchCategories();
    fetchContacts();
  }, []);

  const apiAddress = contactData?.office_address || address;
  const apiPhones = contactData?.phone_number ? [contactData.phone_number] : phones;
  const apiEmail = contactData?.gmail || email;

  return (
    <>
      <style>{`
        /* ══════════════════════════════════════════
           DESIGN TOKENS
        ══════════════════════════════════════════ */
        .ifjona-footer-root {
          --f-bg: #f7f8fa;
          --f-surface: #ffffff;
          --f-border: rgba(0, 0, 0, 0.07);
          --f-border-strong: rgba(0, 0, 0, 0.10);
          --f-green: #1d9e5c;
          --f-green-lt: #25c270;
          --f-green-bg: rgba(29, 158, 92, 0.07);
          --f-green-bg-hover: rgba(29, 158, 92, 0.13);
          --f-text: #4b5563;
          --f-muted: #9ca3af;
          --f-heading: #111827;
          --f-radius: 8px;
          --f-font-body: 'Segoe UI', system-ui, sans-serif;
          font-family: var(--f-font-body);
          background: var(--f-bg);
          color: var(--f-text);
          position: relative;
          overflow: hidden;
        }

        /* Subtle top accent line */
        .ifjona-footer-root::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent 0%, var(--f-green) 30%, var(--f-green-lt) 60%, transparent 100%);
        }

        /* Soft ambient glow */
        .ifjona-footer-root::after {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 55% 35% at 5% 0%, rgba(29,158,92,0.04) 0%, transparent 70%),
            radial-gradient(ellipse 35% 25% at 95% 100%, rgba(29,158,92,0.03) 0%, transparent 60%);
          pointer-events: none;
        }

        .ifjona-footer-root * {
          box-sizing: border-box;
        }

        /* ══════════════════════════════════════════
           MAIN BODY
        ══════════════════════════════════════════ */
        .ifjona-footer-body {
          padding: 40px 0 0;
          position: relative;
          z-index: 1;
        }

        .ifjona-footer-grid {
          display: grid;
          grid-template-columns: 220px repeat(4, 1fr);
          gap: 28px 28px;
          align-items: start;
          padding-bottom: 28px;
        }

        /* ══════════════════════════════════════════
           BRAND COLUMN
        ══════════════════════════════════════════ */
        .ifjona-brand__logo {
          margin-bottom: 14px;
          width: 110px;
          height: auto;
        }
        .ifjona-brand__logo img {
          display: block;
          width: 110px;
          height: auto;
          max-width: 100%;
          object-fit: contain;
        }
        .ifjona-brand__tagline {
          font-size: 12.5px;
          line-height: 1.65;
          color: var(--f-text);
          margin-bottom: 0;
          padding-bottom: 0;
        }

        /* ══════════════════════════════════════════
           LINK COLUMNS
        ══════════════════════════════════════════ */
        .ifjona-footer-col__toggle {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          background: none;
          border: none;
          cursor: default;
          padding: 0;
          margin-bottom: 14px;
        }
        .ifjona-footer-col__title {
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          color: var(--f-heading);
          position: relative;
          padding-bottom: 10px;
          display: block;
        }
        .ifjona-footer-col__title::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 22px;
          height: 2px;
          background: linear-gradient(90deg, var(--f-green), var(--f-green-lt));
          border-radius: 2px;
        }
        .ifjona-footer-col__caret {
          display: none;
          color: var(--f-muted);
        }
        .ifjona-footer-col__list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .ifjona-footer-col__link {
          display: flex;
          align-items: center;
          gap: 6px;
          text-decoration: none;
          font-size: 12.5px;
          color: var(--f-text);
          transition: color 0.2s ease, gap 0.2s ease;
          line-height: 1.4;
        }
        .ifjona-footer-col__link:hover {
          color: var(--f-green);
          gap: 9px;
        }
        .ifjona-footer-col__link-caret {
          font-size: 9px;
          color: var(--f-green);
          opacity: 0;
          transition: opacity 0.2s ease;
          flex-shrink: 0;
        }
        .ifjona-footer-col__link:hover .ifjona-footer-col__link-caret {
          opacity: 1;
        }

        /* ══════════════════════════════════════════
           CATEGORY SKELETON
        ══════════════════════════════════════════ */
        .ifjona-cat-skeleton {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding-top: 4px;
        }
        .ifjona-cat-skeleton__line {
          height: 11px;
          border-radius: 3px;
          background: linear-gradient(90deg,
            rgba(0,0,0,0.04) 25%,
            rgba(0,0,0,0.08) 50%,
            rgba(0,0,0,0.04) 75%
          );
          background-size: 200% 100%;
          animation: shimmer 1.4s infinite;
        }
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* ══════════════════════════════════════════
           CONTACT + SOCIAL DIVIDER ROW
        ══════════════════════════════════════════ */
        .ifjona-contact-social-strip {
          border-top: 1px solid var(--f-border-strong);
          padding: 20px 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          position: relative;
          z-index: 1;
        }

        /* Contact items — left side */
        .ifjona-contact-list {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .ifjona-contact-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 7px 12px 7px 9px;
          border-radius: 8px;
          background: var(--f-surface);
          border: 1px solid var(--f-border);
          box-shadow: 0 1px 2px rgba(0,0,0,0.03);
          transition: border-color 0.2s, box-shadow 0.2s;
          text-decoration: none;
        }
        .ifjona-contact-item:hover {
          border-color: rgba(29,158,92,0.30);
          box-shadow: 0 2px 8px rgba(29,158,92,0.08);
        }

        .ifjona-contact-icon {
          width: 28px;
          height: 28px;
          border-radius: 6px;
          background: var(--f-green-bg);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--f-green);
          font-size: 13px;
          flex-shrink: 0;
          transition: background 0.2s;
        }
        .ifjona-contact-item:hover .ifjona-contact-icon {
          background: var(--f-green-bg-hover);
        }

        .ifjona-contact-info {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }
        .ifjona-contact-label {
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          color: var(--f-muted);
          line-height: 1;
        }
        .ifjona-contact-value {
          font-size: 12px;
          color: var(--f-heading);
          font-weight: 500;
          text-decoration: none;
          line-height: 1.3;
          transition: color 0.2s;
        }
        .ifjona-contact-item:hover .ifjona-contact-value {
          color: var(--f-green);
        }

        /* Separator pip between contact items */
        .ifjona-contact-sep {
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background: var(--f-border-strong);
          flex-shrink: 0;
          margin: 0 2px;
        }

        /* Social — right side */
        .ifjona-social-wrap {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
        }
        .ifjona-social__label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.9px;
          text-transform: uppercase;
          color: var(--f-muted);
          white-space: nowrap;
        }
        .ifjona-social__list {
          display: flex;
          gap: 6px;
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .ifjona-social__link {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 1px solid var(--f-border-strong);
          background: var(--f-surface);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          color: var(--f-text);
          text-decoration: none;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04);
          transition: border-color 0.2s, color 0.2s, background 0.2s, transform 0.18s, box-shadow 0.2s;
        }
        .ifjona-social__link:hover {
          border-color: var(--f-green);
          color: var(--f-green);
          background: var(--f-green-bg);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(29,158,92,0.12);
        }

        /* ══════════════════════════════════════════
           BOTTOM BAR
        ══════════════════════════════════════════ */
        .ifjona-bottom {
          border-top: 1px solid var(--f-border);
          background: rgba(0, 0, 0, 0.02);
          padding: 13px 0;
          position: relative;
          z-index: 1;
        }
        .ifjona-bottom__inner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          margin: 0 auto;
        }
        .ifjona-bottom__copy {
          font-size: 12px;
          color: var(--f-muted);
          line-height: 1.5;
        }
        .ifjona-bottom__copy strong {
          color: var(--f-text);
          font-weight: 600;
        }
        .ifjona-bottom__payment {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .ifjona-bottom__payment-label {
          font-size: 11px;
          color: var(--f-muted);
          white-space: nowrap;
        }
        .ifjona-bottom__payment img {
          max-height: 22px;
          width: auto;
          opacity: 0.80;
        }

        /* ══════════════════════════════════════════
           RESPONSIVE — TABLET
        ══════════════════════════════════════════ */
        @media (max-width: 1199px) {
          .ifjona-footer-grid {
            grid-template-columns: 190px repeat(4, 1fr);
            gap: 24px 16px;
          }
        }

        @media (max-width: 991px) {
          .ifjona-footer-grid {
            grid-template-columns: 1fr 1fr;
            padding-bottom: 24px;
          }
          .ifjona-brand {
            grid-column: 1 / -1;
          }
          .ifjona-footer-body {
            padding: 32px 0 0;
          }
          .ifjona-contact-list {
            gap: 6px;
          }
        }

        /* ══════════════════════════════════════════
           RESPONSIVE — MOBILE
        ══════════════════════════════════════════ */
        @media (max-width: 640px) {
          .ifjona-footer-grid {
            grid-template-columns: 1fr;
            gap: 0;
            padding-bottom: 0;
          }
          .ifjona-footer-body {
            padding: 24px 0 0;
          }
          .ifjona-brand {
            grid-column: 1;
            margin-bottom: 8px;
          }

          /* Collapsible columns on mobile */
          .ifjona-footer-col {
            border-bottom: 1px solid var(--f-border);
            padding: 11px 0;
          }
          .ifjona-footer-col__toggle {
            cursor: pointer;
            margin-bottom: 0;
          }
          .ifjona-footer-col__caret {
            display: block;
          }
          .ifjona-footer-col__title::after {
            display: none;
          }
          .ifjona-footer-col__title {
            padding-bottom: 0;
          }
          .ifjona-footer-col__list {
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.3s ease, margin-top 0.2s;
          }
          .ifjona-footer-col__list--open {
            max-height: 500px;
            margin-top: 12px;
          }

          .ifjona-contact-social-strip {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
            padding: 18px 0;
          }

          .ifjona-contact-list {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
            width: 100%;
          }
          .ifjona-contact-sep {
            display: none;
          }
          .ifjona-contact-item {
            width: 100%;
          }

          .ifjona-social-wrap {
            width: 100%;
            justify-content: flex-start;
          }

          .ifjona-bottom__inner {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
        }
      `}</style>

      <footer className="ifjona-footer-root">
        {/* ── Main body ── */}
        <div className="ifjona-footer-body">
          <div className="container container-lg">

            {/* Top grid: brand + nav columns */}
            <div className="ifjona-footer-grid">
              {/* Brand */}
              <div className="ifjona-brand">
                <div className="ifjona-brand__logo">
                  <Link to="/" aria-label={brand}>
                    <img src={logo} alt={brand} />
                  </Link>
                </div>
                <p className="ifjona-brand__tagline">{tagline}</p>
              </div>

              {/* Static link columns */}
              {staticColumns.map((c) => (
                <FooterColumn key={c.title} title={c.title} links={c.links} />
              ))}

              {/* Categories column */}
              <div className="ifjona-footer-col">
                {catLoading ? (
                  <div className="ifjona-cat-skeleton">
                    {[80, 65, 90, 70, 75, 60].map((w, i) => (
                      <div
                        key={i}
                        className="ifjona-cat-skeleton__line"
                        style={{ width: `${w}%`, animationDelay: `${i * 0.1}s` }}
                      />
                    ))}
                  </div>
                ) : dbCategories.length === 0 ? (
                  <p style={{ fontSize: 12, color: "var(--f-muted)" }}>No categories found.</p>
                ) : (
                  <FooterColumn
                    title="Shop Categories"
                    links={dbCategories.map((cat) => ({
                      label: cat.name,
                      to: cat.id ? `/shop?cat=${cat.id}` : "/shop",
                    }))}
                  />
                )}
              </div>
            </div>

            {/* ── Contact + Social strip ── */}
            <div className="ifjona-contact-social-strip">

              {/* LEFT — contact items */}
              <div className="ifjona-contact-list">
                {/* Address */}
                <div className="ifjona-contact-item">
                  <div className="ifjona-contact-icon">
                    <i className="ph-fill ph-map-pin" />
                  </div>
                  <div className="ifjona-contact-info">
                    <span className="ifjona-contact-label">Address</span>
                    <span className="ifjona-contact-value">{apiAddress}</span>
                  </div>
                </div>

                <div className="ifjona-contact-sep" />

                {/* Phone */}
                <div className="ifjona-contact-item">
                  <div className="ifjona-contact-icon">
                    <i className="ph-fill ph-phone-call" />
                  </div>
                  <div className="ifjona-contact-info">
                    <span className="ifjona-contact-label">Phone</span>
                    {apiPhones.map((p) => (
                      <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="ifjona-contact-value">
                        {p}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="ifjona-contact-sep" />

                {/* Email */}
                <div className="ifjona-contact-item">
                  <div className="ifjona-contact-icon">
                    <i className="ph-fill ph-envelope" />
                  </div>
                  <div className="ifjona-contact-info">
                    <span className="ifjona-contact-label">Email</span>
                    <a href={`mailto:${apiEmail}`} className="ifjona-contact-value">
                      {apiEmail}
                    </a>
                  </div>
                </div>
              </div>

              {/* RIGHT — social icons */}
              <div className="ifjona-social-wrap">
                <span className="ifjona-social__label">Follow Us</span>
                <ul className="ifjona-social__list">
                  {socials.map((s) => (
                    <li key={s.href}>
                      <a
                        href={s.href}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={s.label}
                        className="ifjona-social__link"
                      >
                        <i className={s.icon} />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>{/* /container */}
        </div>{/* /footer-body */}

        {/* ── Bottom bar ── */}
        <div className="ifjona-bottom">
          <div className="container container-lg ifjona-bottom__inner">
            <p className="ifjona-bottom__copy">
              <strong>{brand}</strong> © {year}. All Rights Reserved. Operated by{" "}
              <strong>{licenseCompany}</strong>.
            </p>
            <div className="ifjona-bottom__payment">
              <span className="ifjona-bottom__payment-label">We Accept</span>
              <img src={paymentImg} alt="Payment Methods" />
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default FooterOne;